import {
  ClipboardCheck as CardIconClipboardCheck,
  ClipboardList as CardIconClipboardList,
  NotebookPen as CardIconNotebookPen,
} from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import {
  OCCURRENCE_SEVERITIES,
  OCCURRENCE_SEVERITY_LABELS,
  OCCURRENCE_TYPES,
  OCCURRENCE_TYPE_LABELS,
  answerText,
  compareRecordVersions,
  questionsOf,
  reviewAnswers,
  type OccurrenceSeverity,
  type OccurrenceType,
  type RecordAnswers,
} from '@hue/contracts';
import {
  Button,
  ChoiceChips,
  Input,
  SidePanel,
  StatusText,
  Textarea,
  ToggleChips,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type MutableRefObject, type ReactNode } from 'react';
import {
  getApiError,
  getApiIssues,
  recordsApi,
  type AppointmentSession,
  type ClientChart,
  type SessionOutcomeInput,
} from '../../lib/api.js';
import { formatDate, formatDateTime } from '../../lib/time-zone.js';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { RecordEditor } from './client-records.js';
import { HealthPanel } from './health-panel.js';
import { RecordForm } from './record-form.js';
import { Notice, Toggle } from './shared.js';
import { SessionFacts, Timeline, type TimelineFilter } from './timeline.js';
import { blocksOf, chartQueryKey, serviceTemplatesOf } from './helpers.js';

const CARD = 'rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft';

/// O que a página do atendimento abre no painel lateral: o prontuário, ou uma ficha para ler ou
/// atualizar, sem sair do atendimento.
export type RecordsPanel =
  { kind: 'chart' } | { kind: 'read'; templateId: string } | { kind: 'edit'; templateId: string };

/// "Antes de começar": quanto tempo passou, a última sessão do mesmo serviço, a ficha do serviço em
/// destaque e o que mudou nas fichas desde então.
export function SessionBefore({
  session,
  chart,
  timeZone,
  canManage,
  onPanel,
}: {
  session: AppointmentSession;
  chart: ClientChart;
  timeZone: string;
  canManage: boolean;
  onPanel: (panel: RecordsPanel) => void;
}) {
  const { lastVisit, lastSameService } = session.summary;
  const templates = serviceTemplatesOf(chart, session.appointment.serviceId);
  const featured = templates[0];
  const others = templates.slice(1);
  const featuredRecord = featured
    ? chart.records.find((record) => record.templateId === featured.id)
    : undefined;
  const current = featuredRecord?.versions.at(-1);
  const featuredBlocks = current ? blocksOf(chart, current.templateVersionId) : [];
  const highlights = current
    ? questionsOf(featuredBlocks)
        .map((question) => ({
          question,
          answer: answerText(question, current.answers[question.id]),
        }))
        .filter((item) => item.answer !== null && item.question.themeKey)
        .slice(0, 4)
    : [];
  const since = lastSameService?.startsAt;
  const changes = since
    ? chart.records.flatMap((record) => {
        const template = chart.templates.find((item) => item.id === record.templateId);
        return record.versions.flatMap((version, index) => {
          if (version.recordedAt <= since || !template) return [];
          const before = record.versions[index - 1];
          return [
            {
              key: version.id,
              title: `${template.displayName} · versão ${String(version.version)}`,
              meta: `gravada em ${formatDate(version.recordedAt, timeZone)} por ${version.recordedByDisplayName}`,
              rows: compareRecordVersions(
                before
                  ? { blocks: blocksOf(chart, before.templateVersionId), answers: before.answers }
                  : { blocks: [], answers: {} },
                { blocks: blocksOf(chart, version.templateVersionId), answers: version.answers },
              ),
            },
          ];
        });
      })
    : [];

  return (
    <section aria-labelledby="before-title" className={`${CARD} grid gap-4`}>
      <div>
        <ContentCardHeader
          title="Antes de começar"
          id="before-title"
          icon={<CardIconClipboardList className="size-4" />}
        />
        <p className="mt-1 text-xs text-content-muted">
          {lastVisit
            ? 'O que aconteceu desde a última vez, para quem vai atender hoje.'
            : 'Primeira visita do cliente: não há sessão anterior para comparar.'}
        </p>
      </div>
      {lastVisit ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Stat
            label="Desde a última visita"
            days={lastVisit.days}
            detail={`${formatDate(lastVisit.startsAt, timeZone)} · ${lastVisit.serviceDisplayName} · ${lastVisit.professionalDisplayName}`}
          />
          {lastSameService && lastSameService.appointmentId !== lastVisit.appointmentId ? (
            <Stat
              label={`Desde a última ${session.appointment.serviceDisplayName.toLowerCase()}`}
              days={lastSameService.days}
              detail={`${formatDate(lastSameService.startsAt, timeZone)} · ${lastSameService.professionalDisplayName}`}
            />
          ) : null}
        </div>
      ) : null}

      {featured ? (
        <div className="grid gap-3 rounded-xl border border-brand-500/40 bg-brand-500/5 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-content-muted">
                Ficha deste serviço
              </p>
              <h3 className="text-base font-semibold">
                {featured.displayName}
                {current ? ` · versão ${String(current.version)}` : ''}
              </h3>
            </div>
            {current ? null : <StatusText tone="warning">Não preenchida</StatusText>}
          </div>
          {highlights.length ? (
            <dl className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
              {highlights.map(({ question, answer }) => (
                <div key={question.id} className="contents">
                  <dt className="text-content-muted">{question.label}</dt>
                  <dd className="m-0 font-semibold">{answer}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            {current ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onPanel({ kind: 'read', templateId: featured.id })}
              >
                Ler ficha inteira
              </Button>
            ) : null}
            {canManage && !session.locked ? (
              <Button size="sm" onClick={() => onPanel({ kind: 'edit', templateId: featured.id })}>
                {current ? 'Atualizar ficha' : 'Preencher ficha'}
              </Button>
            ) : null}
            {others.map((template) => (
              <Button
                key={template.id}
                size="sm"
                variant="ghost"
                onClick={() => onPanel({ kind: 'read', templateId: template.id })}
              >
                {template.displayName}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {lastSameService ? (
        <div className="grid gap-2 rounded-xl border border-surface-border bg-surface-elevated/40 p-4">
          <h3 className="text-sm font-semibold">
            Na última {session.appointment.serviceDisplayName.toLowerCase()}, em{' '}
            {formatDate(lastSameService.startsAt, timeZone)}
          </h3>
          <SessionFacts visit={lastSameService} withHighlight={false} />
          {lastSameService.session?.outcome?.highlight ? (
            <Notice tone="warning">
              <strong>Deixado para esta sessão:</strong> “
              {lastSameService.session.outcome.highlight}” —{' '}
              {lastSameService.session.outcome.recordedByDisplayName}
            </Notice>
          ) : null}
        </div>
      ) : null}

      {changes.length ? (
        <div className="grid gap-2">
          <h3 className="text-sm font-semibold">
            Mudou nas fichas desde {formatDate(since!, timeZone)}
          </h3>
          {changes.map((change) => (
            <div key={change.key} className="grid gap-1">
              <p className="text-xs text-content-muted">
                <strong className="text-content">{change.title}</strong> · {change.meta}
              </p>
              {change.rows.length ? (
                <div className="overflow-x-auto rounded-xl border border-surface-border">
                  <table className="w-full min-w-[28rem] border-collapse text-left text-sm">
                    <caption className="sr-only">Respostas que mudaram</caption>
                    <thead>
                      <tr className="border-b border-surface-border text-xs text-content-muted">
                        <th className="px-3 py-2 font-semibold">Pergunta</th>
                        <th className="px-3 py-2 font-semibold">Antes</th>
                        <th className="px-3 py-2 font-semibold">Depois</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-border">
                      {change.rows.map((row) => (
                        <tr key={row.questionId}>
                          <td className="px-3 py-2 text-content-muted">{row.label}</td>
                          <td className="px-3 py-2 text-danger line-through decoration-danger/50">
                            {row.outsideTemplate === 'BEFORE'
                              ? '(pergunta não existia)'
                              : (row.before ?? '—')}
                          </td>
                          <td className="px-3 py-2 font-semibold text-positive">
                            {row.outsideTemplate === 'AFTER'
                              ? '(pergunta saiu do modelo)'
                              : (row.after ?? '—')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-content-faint">Gravada sem mudar respostas.</p>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function Stat({ label, days, detail }: { label: string; days: number; detail: string }) {
  return (
    <div className="grid gap-1 rounded-xl border border-surface-border p-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-content-muted">
        {label}
      </span>
      <span className="text-2xl font-bold tabular-nums tracking-[-.03em]">
        {days}{' '}
        <small className="text-sm font-semibold text-content-muted">
          {days === 1 ? 'dia' : 'dias'}
        </small>
      </span>
      <span className="text-xs text-content-muted">{detail}</span>
    </div>
  );
}

/// A checagem pré-sessão com botão próprio. Não trava nada e pode ser refeita até concluir; as
/// respostas e as anotações ficam na sessão, sem criar versão da ficha.
export function SessionCheck({
  session,
  canManage,
  timeZone,
  today,
  onSaved,
  onUpdateRecord,
}: {
  session: AppointmentSession;
  canManage: boolean;
  timeZone: string;
  today: string;
  onSaved: () => Promise<void>;
  /// Abre a ficha do serviço para atualizar; nulo quando o serviço não tem ficha.
  onUpdateRecord: (() => void) | null;
}) {
  const snackbar = useSnackbar();
  const saved = session.session?.check ?? null;
  const templates = session.checkTemplates;
  const [templateId, setTemplateId] = useState<string | undefined>(
    saved?.templateId ?? (templates.length === 1 ? templates[0]!.id : undefined),
  );
  const [editing, setEditing] = useState(!saved);
  const [answers, setAnswers] = useState<RecordAnswers>(saved?.answers ?? {});
  const [notes, setNotes] = useState(saved?.notes ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const chosen = templates.find((item) => item.id === templateId);
  const readOnly = !canManage || session.locked;

  async function save() {
    if (!chosen) return;
    setRequestError(undefined);
    const review = reviewAnswers(chosen.blocks, answers);
    setErrors(
      Object.fromEntries(review.issues.map((issue) => [issue.blockId ?? '', issue.message])),
    );
    if (review.issues.length) return;
    setSaving(true);
    try {
      await recordsApi.saveSessionCheck(session.appointment.id, {
        templateId: chosen.id,
        templateVersionId: chosen.versionId,
        answers: review.answers,
        notes: notes.trim() || null,
      });
      await onSaved();
      setEditing(false);
      snackbar.success('Checagem salva.');
    } catch (error) {
      const issues = getApiIssues(error);
      if (issues.length) {
        setErrors(Object.fromEntries(issues.map((issue) => [issue.blockId ?? '', issue.message])));
      } else setRequestError(getApiError(error));
    } finally {
      setSaving(false);
    }
  }

  let body: ReactNode;
  if (saved && !editing) {
    const blocks = templates.find((item) => item.id === saved.templateId)?.blocks ?? [];
    const lines = questionsOf(blocks).flatMap((question) => {
      const answer = answerText(question, saved.answers[question.id]);
      return answer === null || !answer.trim() ? [] : [{ label: question.label, answer }];
    });
    body = (
      <div className="grid gap-3">
        <dl className="divide-y divide-surface-border rounded-xl border border-surface-border">
          {lines.map((line) => (
            <div
              key={line.label}
              className="grid gap-1 px-3 py-2 text-sm sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]"
            >
              <dt className="text-content-muted">{line.label}</dt>
              <dd className="m-0 font-semibold">{line.answer}</dd>
            </div>
          ))}
          {saved.notes ? (
            <div className="grid gap-1 px-3 py-2 text-sm sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
              <dt className="text-content-muted">Anotações da sessão</dt>
              <dd className="m-0 whitespace-pre-line">{saved.notes}</dd>
            </div>
          ) : null}
        </dl>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-content-muted">
            Salva em {formatDateTime(saved.recordedAt, timeZone)} por {saved.recordedByDisplayName}.
            As versões das fichas foram retratadas nesse instante.
          </span>
          {readOnly ? null : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Refazer checagem
            </Button>
          )}
        </div>
      </div>
    );
  } else if (!templates.length) {
    body = (
      <p className="text-sm text-content-muted">
        Não há modelo de checagem para {session.appointment.serviceDisplayName}.
        {canManage
          ? ' Quem monta os modelos cria um em Prontuário ▸ Modelos de ficha, com o tipo Checagem pré-sessão.'
          : ''}
      </p>
    );
  } else if (readOnly) {
    body = (
      <p className="text-sm text-content-muted">
        {session.locked
          ? 'Atendimento concluído sem checagem.'
          : 'A checagem ainda não foi feita. Quem pode preencher o prontuário faz aqui.'}
      </p>
    );
  } else {
    body = (
      <div className="grid gap-4">
        {templates.length > 1 ? (
          <ChoiceChips
            label="Qual checagem usar?"
            value={templateId ?? ''}
            options={templates.map((item) => ({ value: item.id, label: item.displayName }))}
            onChange={(value) => {
              setTemplateId(value);
              setAnswers({});
              setErrors({});
            }}
          />
        ) : null}
        {chosen ? (
          <>
            <RecordForm
              blocks={chosen.blocks}
              answers={answers}
              onChange={(value) => {
                setAnswers(value);
                setErrors({});
              }}
              errors={errors}
              today={today}
            />
            <Textarea
              label="Anotações da sessão"
              hint="Dúvidas tiradas e o que o cliente contou hoje."
              value={notes}
              maxLength={4000}
              onChange={(event) => setNotes(event.target.value)}
            />
            <Notice tone="info">
              <div className="grid gap-2">
                <span>
                  Estas respostas e anotações ficam registradas nesta sessão e não criam versão nova
                  da ficha. Se algo mudou de vez, atualize a ficha.
                </span>
                {onUpdateRecord ? (
                  <span>
                    <Button size="sm" variant="outline" onClick={onUpdateRecord}>
                      Atualizar ficha
                    </Button>
                  </span>
                ) : null}
              </div>
            </Notice>
            {requestError ? <RequestAlert message={requestError} /> : null}
            <div className="flex flex-wrap justify-end gap-2">
              {saved ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setAnswers(saved.answers);
                    setNotes(saved.notes ?? '');
                    setErrors({});
                  }}
                >
                  Cancelar
                </Button>
              ) : null}
              <Button loading={saving} onClick={() => void save()}>
                Salvar checagem
              </Button>
            </div>
          </>
        ) : (
          <p className="text-xs text-content-muted">Escolha a checagem para começar.</p>
        )}
      </div>
    );
  }

  return (
    <section aria-labelledby="check-title" className={`${CARD} grid gap-4`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <ContentCardHeader
            title="Checagem pré-sessão"
            id="check-title"
            icon={<CardIconClipboardCheck className="size-4" />}
          />
          <p className="mt-1 text-xs text-content-muted">
            Um minuto com o cliente antes de começar. Não trava o atendimento.
          </p>
        </div>
        {saved ? (
          <StatusText tone="positive">Feita</StatusText>
        ) : templates.length ? (
          <StatusText tone="neutral">Não feita</StatusText>
        ) : null}
      </div>
      {body}
    </section>
  );
}

interface OutcomeDraft {
  occurrence: boolean;
  type: OccurrenceType | '';
  severity: OccurrenceSeverity | '';
  description: string;
  conduct: string;
  productIds: string[];
  alert: boolean;
  alertText: string;
  highlight: string;
}

function draftOf(session: AppointmentSession): OutcomeDraft {
  const outcome = session.session?.outcome;
  const occurrence = outcome?.occurrence;
  return {
    occurrence: Boolean(occurrence),
    type: occurrence?.type ?? '',
    severity: occurrence?.severity ?? '',
    description: occurrence?.description ?? '',
    conduct: occurrence?.conduct ?? '',
    productIds: occurrence?.products.map((product) => product.productId) ?? [],
    alert: !outcome?.alertId,
    alertText: '',
    highlight: outcome?.highlight ?? '',
  };
}

/// O que a página faz com o registro não salvo ao concluir: grava junto, ou diz que precisa de
/// correção antes.
export interface PendingOutcome {
  save: () => Promise<boolean>;
}

/// O registro da sessão com botão próprio: intercorrência com o produto envolvido, alerta
/// permanente criado uma vez e destaque para a próxima sessão.
export function SessionOutcome({
  session,
  canManage,
  timeZone,
  onSaved,
  pendingRef,
}: {
  session: AppointmentSession;
  canManage: boolean;
  timeZone: string;
  onSaved: () => Promise<void>;
  /// A página guarda aqui como gravar o que não foi salvo, para concluir gravar junto.
  pendingRef: MutableRefObject<PendingOutcome | null>;
}) {
  const snackbar = useSnackbar();
  const saved = session.session?.outcome ?? null;
  const [draft, setDraft] = useState<OutcomeDraft>(() => draftOf(session));
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const readOnly = !canManage || session.locked;
  const alertCreated = Boolean(saved?.alertId);

  function change(patch: Partial<OutcomeDraft>) {
    setDraft({ ...draft, ...patch });
    setDirty(true);
    setErrors({});
  }

  function validate(): SessionOutcomeInput | null {
    const found: Record<string, string> = {};
    if (draft.occurrence) {
      if (!draft.type) found.type = 'Escolha o tipo.';
      if (!draft.severity) found.severity = 'Escolha a gravidade.';
      if (!draft.description.trim()) found.description = 'Descreva o que aconteceu.';
      if (draft.alert && !alertCreated && !draft.alertText.trim()) {
        found.alertText = 'Escreva o alerta, ou desligue a opção.';
      }
    }
    setErrors(found);
    if (Object.keys(found).length) return null;
    return {
      occurrence: draft.occurrence
        ? {
            type: draft.type as OccurrenceType,
            severity: draft.severity as OccurrenceSeverity,
            description: draft.description.trim(),
            conduct: draft.conduct.trim() || null,
            productIds: draft.productIds,
          }
        : null,
      alertText: draft.occurrence && draft.alert && !alertCreated ? draft.alertText.trim() : null,
      highlight: draft.highlight.trim() || null,
    };
  }

  async function save(quiet = false): Promise<boolean> {
    const input = validate();
    if (!input) return false;
    setSaving(true);
    setRequestError(undefined);
    try {
      await recordsApi.saveSessionOutcome(session.appointment.id, input);
      setDirty(false);
      await onSaved();
      if (!quiet) {
        snackbar.success(
          input.occurrence
            ? 'Registro salvo. A intercorrência entrou no Quadro de saúde.'
            : 'Registro salvo.',
        );
      }
      return true;
    } catch (error) {
      setRequestError(getApiError(error));
      return false;
    } finally {
      setSaving(false);
    }
  }

  // Concluir grava junto o que estiver preenchido e não salvo (ADR 0038, adendo de 2026-09-28).
  useEffect(() => {
    pendingRef.current = readOnly
      ? null
      : { save: () => (dirty ? save(true) : Promise.resolve(true)) };
  });

  const products = session.products ?? [];
  const occurrence = saved?.occurrence;

  return (
    <section aria-labelledby="outcome-title" className={`${CARD} grid gap-4`}>
      <div>
        <ContentCardHeader
          title="Registro da sessão"
          id="outcome-title"
          icon={<CardIconNotebookPen className="size-4" />}
        />
        <p className="mt-1 text-xs text-content-muted">
          Intercorrência e o que deixar para a próxima sessão. A intercorrência fica no Quadro de
          saúde para sempre.
        </p>
      </div>
      {readOnly ? (
        saved ? (
          <div className="grid gap-3 text-sm">
            {occurrence ? (
              <div className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3">
                <div className="grid gap-1">
                  <strong>
                    {OCCURRENCE_TYPE_LABELS[occurrence.type]} ·{' '}
                    {OCCURRENCE_SEVERITY_LABELS[occurrence.severity]}
                    {occurrence.products.length
                      ? ` · ${occurrence.products.map((product) => product.displayName).join(', ')}`
                      : ''}
                  </strong>
                  <span>{occurrence.description}</span>
                  {occurrence.conduct ? (
                    <span className="text-content-muted">Conduta: {occurrence.conduct}</span>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="text-content-muted">Sem intercorrência.</p>
            )}
            <p>
              <span className="text-content-muted">Destaque para a próxima sessão: </span>
              {saved.highlight ?? 'nenhum'}
            </p>
            <p className="text-xs text-content-muted">
              Salvo em {formatDateTime(saved.recordedAt, timeZone)} por{' '}
              {saved.recordedByDisplayName}
              {session.locked ? '. Para corrigir, reabra o atendimento.' : '.'}
            </p>
          </div>
        ) : (
          <p className="text-sm text-content-muted">
            {session.locked
              ? 'Concluído sem registro da sessão.'
              : 'Quem pode preencher o prontuário registra a sessão aqui.'}
          </p>
        )
      ) : (
        <div className="grid gap-4">
          <Toggle checked={draft.occurrence} onChange={(value) => change({ occurrence: value })}>
            Houve intercorrência
          </Toggle>
          {draft.occurrence ? (
            <div className="grid gap-4 rounded-xl border border-surface-border bg-surface-elevated/40 p-4">
              <div className="grid gap-1">
                <ChoiceChips
                  label="Tipo"
                  value={draft.type}
                  options={OCCURRENCE_TYPES.map((type) => ({
                    value: type,
                    label: OCCURRENCE_TYPE_LABELS[type],
                  }))}
                  onChange={(value) => change({ type: value as OccurrenceType })}
                />
                {errors.type ? (
                  <p role="alert" className="text-xs text-danger">
                    {errors.type}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-1">
                <ChoiceChips
                  label="Gravidade"
                  value={draft.severity}
                  options={OCCURRENCE_SEVERITIES.map((severity) => ({
                    value: severity,
                    label: OCCURRENCE_SEVERITY_LABELS[severity],
                  }))}
                  onChange={(value) => change({ severity: value as OccurrenceSeverity })}
                />
                {errors.severity ? (
                  <p role="alert" className="text-xs text-danger">
                    {errors.severity}
                  </p>
                ) : null}
              </div>
              {session.products ? (
                <div className="grid gap-1">
                  {products.length ? (
                    <ToggleChips
                      label="Produto envolvido"
                      itemNoun="produtos"
                      selected={draft.productIds}
                      options={products.map((product) => ({
                        value: product.productId,
                        label: product.displayName,
                      }))}
                      onChange={(productIds) => change({ productIds })}
                    />
                  ) : null}
                  <p className="text-xs text-content-muted">
                    {products.length
                      ? 'Do material deste atendimento. Pode marcar mais de um, ou nenhum se não souber.'
                      : 'Ainda não há material neste atendimento para apontar o produto envolvido.'}
                  </p>
                </div>
              ) : null}
              <Textarea
                label="O que aconteceu"
                value={draft.description}
                error={errors.description}
                maxLength={2000}
                onChange={(event) => change({ description: event.target.value })}
              />
              <Textarea
                label="Conduta"
                value={draft.conduct}
                maxLength={2000}
                onChange={(event) => change({ conduct: event.target.value })}
              />
              {alertCreated ? (
                <p className="text-xs text-content-muted">
                  O alerta permanente desta intercorrência já foi criado. Para encerrá-lo, use o
                  Quadro de saúde.
                </p>
              ) : (
                <>
                  <Toggle checked={draft.alert} onChange={(value) => change({ alert: value })}>
                    Criar alerta permanente no Quadro de saúde
                  </Toggle>
                  {draft.alert ? (
                    <Input
                      label="Texto do alerta"
                      value={draft.alertText}
                      error={errors.alertText}
                      maxLength={500}
                      placeholder="Ex.: Não usar oxidante acima de 20 vol."
                      onChange={(event) => change({ alertText: event.target.value })}
                    />
                  ) : null}
                </>
              )}
              {draft.type === 'ALLERGIC_REACTION' ? (
                <Notice tone="warning">
                  Reação alérgica: confirme com o cliente e atualize a pergunta de alergia da ficha.
                  O sistema não muda a ficha sozinho.
                </Notice>
              ) : null}
            </div>
          ) : null}
          <Textarea
            label="Destaque para a próxima sessão"
            hint="Aparece no “Antes de começar” da próxima sessão deste serviço."
            value={draft.highlight}
            maxLength={1000}
            onChange={(event) => change({ highlight: event.target.value })}
          />
          {requestError ? <RequestAlert message={requestError} /> : null}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-content-muted">
              {saved
                ? `Salvo em ${formatDateTime(saved.recordedAt, timeZone)} por ${saved.recordedByDisplayName}${dirty ? ' · há mudanças não salvas' : ''}`
                : dirty
                  ? 'Não salvo. Concluir grava junto.'
                  : ''}
            </span>
            <Button variant="outline" loading={saving} onClick={() => void save()}>
              Salvar registro
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/// O Prontuário ou uma ficha num painel lateral: para consultar a partir da Agenda ou atualizar a
/// ficha durante o atendimento, sem trocar de tela.
export function RecordsSidePanel({
  panel,
  clientId,
  timeZone,
  today,
  canManage,
  consents,
  onClose,
  onPanel,
  onOpenClientPage,
}: {
  panel: RecordsPanel | null;
  clientId: string;
  timeZone: string;
  today: string;
  canManage: boolean;
  consents?: ReactNode;
  onClose: () => void;
  /// Troca o que o painel mostra: da ficha lida para a edição, ou da linha do tempo para a ficha.
  onPanel: (panel: RecordsPanel) => void;
  onOpenClientPage?: (() => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<TimelineFilter>('all');
  const [full, setFull] = useState(false);
  const chart = useQuery({
    queryKey: chartQueryKey(clientId),
    queryFn: () => recordsApi.getChart(clientId),
    enabled: panel !== null,
    retry: false,
  });
  const data = chart.data;
  const template =
    panel && panel.kind !== 'chart'
      ? data?.templates.find((item) => item.id === panel.templateId)
      : undefined;
  const record = template
    ? data?.records.find((item) => item.templateId === template.id)
    : undefined;
  const title = !panel
    ? ''
    : panel.kind === 'chart'
      ? `Prontuário · ${data?.client.displayName ?? ''}`
      : panel.kind === 'edit'
        ? `${record ? 'Atualizar' : 'Preencher'} ${template?.displayName ?? 'ficha'}`
        : `${template?.displayName ?? 'Ficha'}${record ? ` · versão ${String(record.currentVersion)}` : ''}`;

  let body: ReactNode = null;
  if (chart.isLoading) body = <LoadingCard label="Carregando prontuário" />;
  else if (chart.error || !data) body = <ErrorCard message={getApiError(chart.error)} />;
  else if (panel?.kind === 'chart') {
    body = (
      <div className="grid gap-4">
        <HealthPanel chart={data} timeZone={timeZone} compact />
        <Timeline
          chart={data}
          timeZone={timeZone}
          filter={filter}
          full={full}
          onFilter={setFilter}
          onFull={setFull}
          onOpenVersion={(id) => onPanel({ kind: 'read', templateId: id })}
        />
      </div>
    );
  } else if (panel?.kind === 'edit' && template) {
    body = (
      <RecordEditor
        key={`${template.id}-${String(record?.currentVersion ?? 0)}`}
        chart={data}
        template={template}
        record={record}
        today={today}
        consents={consents}
        onCancel={onClose}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'records'] });
          onClose();
        }}
      />
    );
  } else if (panel?.kind === 'read' && template) {
    const current = record?.versions.at(-1);
    body = current ? (
      <dl className="divide-y divide-surface-border rounded-xl border border-surface-border">
        {questionsOf(blocksOf(data, current.templateVersionId)).map((question) => {
          const answer = answerText(question, current.answers[question.id]);
          return (
            <div key={question.id} className="grid gap-1 px-3 py-2 text-sm">
              <dt className="text-content-muted">{question.label}</dt>
              <dd className={answer === null ? 'm-0 text-content-faint' : 'm-0 font-semibold'}>
                {answer ?? 'Não respondida'}
              </dd>
            </div>
          );
        })}
      </dl>
    ) : (
      <p className="text-sm text-content-muted">Ficha ainda não preenchida.</p>
    );
  }

  const footer =
    panel?.kind === 'read' && template ? (
      <>
        <Button variant="outline" onClick={onClose}>
          Fechar
        </Button>
        {canManage ? (
          <Button onClick={() => onPanel({ kind: 'edit', templateId: template.id })}>
            {record ? 'Atualizar ficha' : 'Preencher ficha'}
          </Button>
        ) : null}
      </>
    ) : panel?.kind === 'chart' && onOpenClientPage ? (
      <Button variant="outline" onClick={onOpenClientPage}>
        Abrir página do cliente
      </Button>
    ) : undefined;

  return (
    <SidePanel
      open={panel !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      description={
        panel?.kind === 'edit'
          ? 'Salvar grava uma versão nova; a anterior fica no histórico. Você continua no atendimento.'
          : panel?.kind === 'chart'
            ? 'Linha do tempo, fichas e alertas, sem sair de onde você está.'
            : undefined
      }
      footer={footer}
    >
      {body}
    </SidePanel>
  );
}
